<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>
  <div class="page-wrapper">
    <!-- Page Content -->
    <div class="content container-fluid">
      
      <!-- Page Header -->
      <div class="page-header">
        <div class="row align-items-center">
          <div class="col">
            <h3 class="page-title">Host Selection</h3>
            <ul class="breadcrumb">
              <li class="breadcrumb-item">Employees</li>
              <li class="breadcrumb-item active">Host Selection</li>
            </ul>
          </div>
          <div class="col-auto float-end ms-auto">
            <a href="javascript:;" class="btn add-btn" data-bs-toggle="modal" data-bs-target="#add_host_modal">
              <i class="fa-solid fa-plus"></i> Add Host
            </a>
          </div>
        </div>
      </div>
      <!-- /Page Header -->

      <div class="row">
        <div class="col-md-12">
          <div v-if="!loading">
            <div class="table-responsive">
              <a-table 
                class="stripped table-hover" 
                :columns="columns" 
                :data-source="selectedHosts" 
                :pagination="false" 
                v-if="selectedHosts.length" 
                :scroll="{ x: 'max-content' }"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'name'">
                    <h2 class="table-avatar">
                      <a href="javascript:;" class="avatar">
                        <img :src="getImage(record.profile_image_path)" alt="User Image" v-if="record.profile_image_path" />
                        <img src="../../../../assets/img/avatar/avatar-29.png" alt="User Image" v-else />
                      </a>
                      <div class="text-end dropdown-container">
                          <action-dropdown @banked-time="bankedTimeClicked"
                            variant="text"
                            :employee="record"
                            :showViewProfile="true"
                            :monitoringText="{ monitor: 'Start Monitoring', monitored: 'Stop Monitoring' }"
                            :reportAsEvent="true"
                            @edit="editClicked"
                            @leaves="leavesClicked"
                            @attendance="attendanceClicked"
                            @sick-leave="sickLeaveClicked"
                            @toggle-monitoring="monitorClicked"
                            @add-face-template="addFaceTemplateClicked"
                            @face-templates="faceTemplatesClicked"
                            @report="viewReport"
                            @reset-password="resetPasswordClicked"
                            @assign-employee="assignEmployeeClicked"
                          />
                      </div>
                    </h2>
                  </template>
                  <template v-else-if="column.key === 'action'">
                    <div class="text-end">
                       <a href="javascript:;" class="btn btn-sm btn-danger" @click="removeHost(record.id)">
                         <i class="fa-regular fa-trash-can m-r-5"></i> Remove
                       </a>
                    </div>
                  </template>
                </template>
              </a-table>
              <div v-else>
                <div class="card text-center">
                  <div class="card-body">
                    <img src="../../../../assets/img/avatar/no-data.png" alt="no-data" class="responsive-img">
                    <p class="mt-3">No hosts have been added.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex justify-content-center text-center mt-5" v-else>
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- /Page Content -->

    <!-- Add Host Modal -->
    <div id="add_host_modal" class="modal custom-modal fade" role="dialog" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Add Host</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" ref="closeModalBtn">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="addHosts">
              <div class="input-block mb-3">
                <label class="col-form-label">Select Employees <span class="text-danger">*</span></label>
                <v-autocomplete
                  v-model="modalSelectedHosts"
                  :items="filteredEmployeeOptions"
                  item-title="text"
                  item-value="id"
                  multiple
                  chips
                  clearable
                  variant="outlined"
                  density="comfortable"
                  placeholder="Search & Select employees"
                ></v-autocomplete>
              </div>
              <div class="submit-section">
                <button class="btn btn-primary submit-btn" type="submit" :disabled="saving">
                  <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <!-- /Add Host Modal -->

    <edit-employee-modal ref="editEmployee" @editedUser="fetchData"></edit-employee-modal>
    <add-face-template-modal ref="addface"></add-face-template-modal>
    <face-templates-modal ref="faceTemplates"></face-templates-modal>
    <sick-leave-modal ref="sickLeave"></sick-leave-modal>
    <employee-report-modal ref="employeeReportModal"></employee-report-modal>
    <reset-password-modal ref="resetPasswordModal"></reset-password-modal>
    <assign-employee-modal ref="assignEmployeeModal" @updated="fetchData"></assign-employee-modal>

  </div>
    <view-banked-time-details-modal ref="viewBankedTimeDetails"></view-banked-time-details-modal>
</template>

<script>
import { VUE_APP_API_BASE_URL } from "../../../../config";
import axios from "axios";
import { Modal } from 'bootstrap';
import { getImage } from "@/utils/util-funcitons";
import "vue-toast-notification/dist/theme-default.css";
import ActionDropdown from "@/components/action-dropdown.vue";
import FaceTemplatesModal from "@/components/modal/face-templates-modal.vue";
import AssignEmployeeModal from "@/components/modal/assign-employee-modal.vue";
import { monitorMixin } from "@/mixins/monitorMixin";
import { dateRangeStart, dateRangeEnd } from '@/utils/util-var';

const columns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    sorter: {
      compare: (a, b) => {
        let nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        let nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA > nameB ? -1 : nameB > nameA ? 1 : 0;
      },
    },
  },
  {
    title: "Employee ID",
    dataIndex: "employee_code",
    key: "employee_code",
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
  },
  {
    title: "Action",
    key: "action",
    align: "right",
  },
];

export default {
  mixins: [monitorMixin],
  components: {
    ActionDropdown,
    FaceTemplatesModal,
    AssignEmployeeModal
  },
  data() {
    return {
      startDate: dateRangeStart,
      endDate: dateRangeEnd,
      employeeOptions: [],
      selectedHosts: [],
      modalSelectedHosts: [],
      companyRoles: [],
      columns,
      loading: false,
      saving: false,
    };
  },
  computed: {
    filteredEmployeeOptions() {
      // Filter out users that are already selected
      const selectedIds = this.selectedHosts.map((h) => h.id);
      return this.employeeOptions.filter((opt) => !selectedIds.includes(opt.id));
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    bankedTimeClicked(record) {
      if(this.$refs.viewBankedTimeDetails) {
        this.$refs.viewBankedTimeDetails.updateValues(record, {});
      }
    },

    async getEmployees() {
      try {
        const response = await axios.get(`${VUE_APP_API_BASE_URL}/user/user-list`);
        if (response.data.status) {
          this.employeeOptions = response.data.data.map(element => ({
            id: element.id,
            text: `${element.first_name} ${element.last_name}`
          }));
        }
        if (typeof this._applyPending === 'function') {
          this._applyPending();
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getSelectedHosts() {
      try {
        const hostsRes = await axios.get(`${VUE_APP_API_BASE_URL}/user/selected-hosts`);
        if (hostsRes.data && hostsRes.data.data) {
          this.selectedHosts = hostsRes.data.data.map(host => ({
            ...host,
            name: `${host.first_name} ${host.last_name}`
          }));
        }
      } catch (error) {
        console.error("Error fetching hosts:", error);
      }
    },
    async fetchData() {
      this.loading = true;
      try {
        await Promise.all([
          this.getEmployees(),
          this.getSelectedHosts(),
          this.getCompanyRoles()
        ]);
      } finally {
        this.loading = false;
      }
    },
    getImage,
    companyIdToCompanyRole(id) {
      const role = this.companyRoles.find(item => item.id === id);
      return role ? role.name : '';
    },
    async getCompanyRoles() {
      try {
        const response = await axios.get(`${VUE_APP_API_BASE_URL}/company-role/get`);
        if (response.data.status) {
          this.companyRoles = response.data.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async removeHost(id) {
      this.loading = true;
      try {
        const response = await axios.post(`${VUE_APP_API_BASE_URL}/admin/delete-host`, {
          host_id: id,
        });

        if (response.data && response.data.status) {
          this.$toast.success("Host removed successfully", {
            position: "top-right",
          });
          await this.fetchData();
        } else {
          this.$toast.error(response.data.message || "Failed to remove host", {
            position: "top-right",
          });
          this.loading = false;
        }
      } catch (error) {
        console.error("Error removing host:", error);
        this.loading = false;
      }
    },
    addHosts() {
      if (this.modalSelectedHosts.length === 0) {
         this.$toast.warning("Please select at least one employee", {
            position: "top-right",
          });
         return;
      }
      
      const newIds = this.modalSelectedHosts.map((h) => h.id || h);
      const currentIds = this.selectedHosts.map((h) => h.id);
      const hostIds = [...currentIds, ...newIds];
      
      this.saveHosts(hostIds, true);
    },
    async saveHosts(hostIds, isFromModal = false) {
      if (isFromModal) this.saving = true;
      else this.loading = true;
      
      try {
        const response = await axios.post(`${VUE_APP_API_BASE_URL}/admin/select-hosts`, {
          host_ids: hostIds,
        });

        if (response.data && response.data.status) {
          this.$toast.success("Hosts updated successfully", {
            position: "top-right",
          });
          
          if (isFromModal) {
            this.modalSelectedHosts = [];
            // Close the modal using the ref to the close button
            if (this.$refs.closeModalBtn) {
               this.$refs.closeModalBtn.click();
            }
          }
          
          // Refresh list to get accurate host data
          await this.fetchData();
        } else {
          this.$toast.error(response.data.message || "Failed to update hosts", {
            position: "top-right",
          });
        }
      } catch (error) {
        console.error("Error updating hosts:", error);
        this.$toast.error("An error occurred while updating hosts", {
          position: "top-right",
        });
      } finally {
        if (isFromModal) this.saving = false;
        else this.loading = false;
      }
    },
    editClicked(record) {
      this.$refs.editEmployee.updateValues(record);
    },
    sickLeaveClicked(record) {
      this.$refs.sickLeave.updateValues(record);
    },
    leavesClicked(record) {
      const routeData = this.$router.resolve({ path: '/employee/leaves-employee', query: { id: record.id } });
      window.open(routeData.href, '_blank');
    },
    attendanceClicked(record) {
      const routeData = this.$router.resolve({ path: '/employee/attendance-employee', query: { id: record.id } });
      window.open(routeData.href, '_blank');
    },
    addFaceTemplateClicked(record) {
      this.$refs.addface.updateValues(record);
    },
    faceTemplatesClicked(employee) {
      this.$refs.faceTemplates.showTemplates(employee);
    },
    viewReport(record) {
      this.$refs.employeeReportModal.updateValues(record.id, this.startDate, this.endDate);
      const modal = new Modal(document.getElementById('employee_report_modal'));
      modal.show();
    },
    resetPasswordClicked(record) {
      this.$refs.resetPasswordModal.updateValues(record);
    },
    assignEmployeeClicked(record) {
      this.$refs.assignEmployeeModal.updateValues(record);
    },
  },
};
</script>

<style scoped>
:deep(.ant-table),
:deep(.ant-table-container),
:deep(.ant-table-content),
:deep(.ant-table-body) {
  overflow: visible !important;
}

.responsive-img {
  width: 100%;
  max-width: 100px;
  height: auto;
}

.avatar {
  cursor: default;
}
</style>
